'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var tslib = require('tslib');
var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var Popup_types = require('./Popup.types.js');
var utils = require('./utils.js');
var utilityFunctions = require('../../utilityFunctions.js');
var PopupContext = require('./PopupContext.js');
var PopupContainer = require('./PopupContainer.js');
var PopupManagerContext = require('./PopupManagerContext.js');
var popup_module = require('./popup.module.scss.js');
var usePopup = require('../../hooks/usePopup/usePopup.js');

var Popup = function (_a) {
    var _b, _c;
    var _d = _a.arrowClassName, arrowClassName = _d === void 0 ? '' : _d, contents = _a.contents, _e = _a.className, className = _e === void 0 ? '' : _e, id = _a.id, initiator = _a.initiator, onHoverOpen = _a.onHoverOpen, _f = _a.showCloseButton, showCloseButton = _f === void 0 ? false : _f, _g = _a.hideArrow, hideArrow = _g === void 0 ? false : _g, onResize = _a.onResize, onClose = _a.onClose, onOpen = _a.onOpen, popperOptions = _a.popperOptions, role = _a.role, _h = _a.sticky, sticky = _h === void 0 ? false : _h, _j = _a.showInitially, showInitially = _j === void 0 ? false : _j, _k = _a.trigger, trigger = _k === void 0 ? Popup_types.PopupTrigger.CLICK : _k;
    var closeCurrent = react.useContext(PopupManagerContext.default).closeCurrent;
    var isClickTrigger = trigger === Popup_types.PopupTrigger.CLICK;
    var isHoverTrigger = trigger === Popup_types.PopupTrigger.HOVER;
    var notifyParent = react.useCallback(function (newDisplay) {
        if (newDisplay && onOpen) {
            onOpen();
        }
        else if (!newDisplay && onClose) {
            onClose();
        }
    }, [onClose, onOpen]);
    var handleOnClose = react.useCallback(function (intiatorRefParam, popupRefParam) {
        var _a;
        if (isHoverTrigger) {
            var doesInitiatorContainActiveElement = utils.doesContainActiveElement(intiatorRefParam === null || intiatorRefParam === void 0 ? void 0 : intiatorRefParam.current);
            var doesPopupContainActiveElement = utils.doesContainActiveElement(popupRefParam === null || popupRefParam === void 0 ? void 0 : popupRefParam.current);
            if (doesInitiatorContainActiveElement ||
                doesPopupContainActiveElement) {
                (_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.blur();
                notifyParent(false);
            }
        }
        return !isHoverTrigger; // never set display to false for hover triggers
    }, [isHoverTrigger, notifyParent]);
    var _l = usePopup.usePopup({
        excludeFromPopupManager: isHoverTrigger,
        hideArrow: hideArrow,
        showCloseButton: showCloseButton,
        id: id,
        onClose: handleOnClose,
        popperOptions: popperOptions,
        role: role,
        showInitially: showInitially || isHoverTrigger,
        sticky: sticky
    }), closePopup = _l.closePopup, isDisplayed = _l.isDisplayed, initiatorProps = _l.initiatorProps, popupProps = _l.popupProps, recalculatePosition = _l.recalculatePosition, togglePopup = _l.togglePopup;
    var ref = initiatorProps.ref, restOfInitiatorProps = tslib.__rest(initiatorProps, ["ref"]);
    var initiatorRef = initiator.ref || ref;
    var popperContainerRef = popupProps.ref;
    var getDisplayClass = function (isVisible) {
        return isVisible ? '' : 'd-none';
    };
    var getHoverContainerClass = function (isHover) {
        return isHover ? "".concat(popup_module.default['popup-hover'], " ").concat(popup_module.default['popup-hover__target']) : '';
    };
    var recalculateAndClosePopups = function () {
        if (isHoverTrigger && onHoverOpen) {
            onHoverOpen();
        }
        recalculatePosition();
        closeCurrent();
    };
    var updatePopperWhenHoverTrigger = isHoverTrigger
        ? recalculateAndClosePopups
        : undefined;
    var isFirstRun = react.useRef(true);
    react.useEffect(function () {
        // don't fire the onClose callback if this is the first time this useEffect fires
        // onOpen should still fire if showInitially is true
        if (isDisplayed || !isFirstRun.current) {
            notifyParent(isDisplayed);
        }
        isFirstRun.current = false;
    }, [isDisplayed, notifyParent]);
    react.useEffect(function () {
        if (onResize) {
            var options_1 = {
                closePopup: closePopup,
                isDisplayed: isDisplayed,
                togglePopup: togglePopup
            };
            var handleResize_1 = function (e) { return onResize(e, options_1); };
            window.addEventListener('resize', handleResize_1);
            return function () { return window.removeEventListener('resize', handleResize_1); };
        }
    }, [closePopup, isDisplayed, onResize, togglePopup]);
    react.useEffect(function () {
        if (isHoverTrigger) {
            var popperContainer_1 = popperContainerRef === null || popperContainerRef === void 0 ? void 0 : popperContainerRef.current;
            var firstFocusableElement_1 = utilityFunctions.getFirstFocusableElement(popperContainer_1);
            var onTabListener_1 = function (e) {
                var _a, _b;
                if (e.key === 'Tab' &&
                    !e.shiftKey &&
                    firstFocusableElement_1 &&
                    utilityFunctions.getDisplayStyle(popperContainer_1) === 'block' &&
                    !utils.doesContainActiveElement(popperContainer_1)) {
                    e.preventDefault();
                    (_a = popperContainer_1 === null || popperContainer_1 === void 0 ? void 0 : popperContainer_1.classList) === null || _a === void 0 ? void 0 : _a.add('d-block');
                    firstFocusableElement_1 === null || firstFocusableElement_1 === void 0 ? void 0 : firstFocusableElement_1.focus();
                    (_b = popperContainer_1 === null || popperContainer_1 === void 0 ? void 0 : popperContainer_1.classList) === null || _b === void 0 ? void 0 : _b.remove('d-block');
                }
            };
            document.addEventListener('keydown', onTabListener_1);
            return function () { return document.removeEventListener('keydown', onTabListener_1); };
        }
        // deps are disabled here because it's falsly identifying refs as necessary dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHoverTrigger]);
    return (jsxRuntime.jsxs(PopupContext.default.Provider, tslib.__assign({ value: {
            closePopup: closePopup,
            isDisplayed: isDisplayed,
            togglePopup: togglePopup
        } }, { children: [initiator &&
                react.cloneElement(initiator, tslib.__assign(tslib.__assign({}, restOfInitiatorProps), { className: "".concat(((_b = initiator === null || initiator === void 0 ? void 0 : initiator.props) === null || _b === void 0 ? void 0 : _b.addClass) || '', " ").concat(((_c = initiator === null || initiator === void 0 ? void 0 : initiator.props) === null || _c === void 0 ? void 0 : _c.className) || '', " ").concat(popup_module.default['popup-hover']), ref: initiatorRef, onClick: isClickTrigger ? togglePopup : undefined, onMouseEnter: updatePopperWhenHoverTrigger, onFocus: updatePopperWhenHoverTrigger })), jsxRuntime.jsx(PopupContainer.default, tslib.__assign({}, popupProps, { arrowClassName: arrowClassName, className: "".concat(className, " ").concat(getHoverContainerClass(isHoverTrigger), " ").concat(getDisplayClass(isDisplayed)), handleClose: closePopup }, { children: contents && contents }))] })));
};
var Popup$1 = Popup;

Object.defineProperty(exports, 'PopupTrigger', {
    enumerable: true,
    get: function () { return Popup_types.PopupTrigger; }
});
exports.default = Popup$1;
//# sourceMappingURL=Popup.js.map
